import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import './ShipmentAndBilling.css';
import Footer from "../Home/Footer";

const ShipmentAndBilling = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Shipping and Billing | AI4Bazaar";
    
    if (isInView) {
      controls.start("visible");
    }

    const handlePopState = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.5
      }
    },
    hover: {
      scale: 1.01,
      transition: { duration: 0.2 }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8 }
    }
  };

  const sections = [
    {
      title: "Billing Information and Process",
      content: [
        "Upon purchasing any service from AI4Bazaar, you will receive an invoice that clearly outlines the details of the service provided, including the scope of work, fees, and applicable taxes. The invoice will be sent to the email address registered with your account.",
        "Invoices are generated as soon as the payment is processed and can be accessed anytime via your AI4Bazaar account dashboard."
      ]
    },
    {
      title: "Payment Methods",
      content: [
        "We accept payments through a variety of secure online methods, including credit/debit cards, net banking, and Razorpay wallet. The payment process is handled through Razorpay to ensure safety and reliability.",
        "For all payments made through Razorpay, a transaction receipt will be sent to you, confirming the successful payment and providing details such as the amount paid, payment date, and method used.",
        "Please ensure that the billing information provided during the payment process is accurate, as it is used to process invoices and any refund or billing issues. AI4Bazaar will not be responsible for delays or issues caused by incorrect billing details."
      ]
    },
    {
      title: "Payment Schedule",
      content: [
        "For services that are billed on a subscription basis (e.g., monthly or annual digital marketing or e-commerce plans), payments will be charged at the start of each billing cycle. You will receive an email reminder before the renewal date, and payment will be automatically processed unless canceled before the renewal date.",
        "For one-time services (e.g., website development), payment is due at the time of service agreement signing or before the commencement of the project, as specified in the service contract."
      ]
    },
    {
      title: "Currency and Taxes",
      content: [
        "All payments are processed in Indian Rupees (INR) unless otherwise specified. Any applicable taxes, including Goods and Services Tax (GST), will be included in the invoice amount as per the current tax regulations.",
        "If your business is registered under GST, please provide your GSTIN at the time of purchase, and the tax invoice will reflect the applicable GST charges."
      ]
    },
    {
      title: "Shipment/Delivery Information",
      content: [
        "AI4Bazaar is a digital service provider, so there is no physical shipment of products. Instead, all services are delivered online, such as website development, e-commerce platform integration, or digital marketing campaigns.",
        "For website development or e-commerce platform projects, the delivery is typically made once the website is fully designed, developed, and approved by the client. A live version of your website will be available for access as soon as deployment is completed.",
        "For digital marketing services, campaigns will be launched and tracked based on the schedule provided in the service contract. Clients can track the progress and performance of campaigns via their AI4Bazaar dashboard."
      ]
    },
    {
      title: "Timeline for Service Delivery",
      content: [
        "The time required for service delivery depends on the scope of the project and will be discussed and agreed upon at the time of purchase. Generally, for standard website development projects, delivery will be made within 10-14 business days from the project start date.",
        "Digital marketing campaigns or e-commerce setups may require additional time for strategy planning, design, and implementation, with an estimated timeline provided during the initial consultation phase."
      ]
    },
    {
      title: "Shipping of Physical Products",
      content: [
        "For clients who purchase any additional physical products, such as branded merchandise or hardware (if applicable), the shipping of these products will follow standard procedures. You will be notified of any shipping charges, estimated delivery times, and tracking information once the items are dispatched.",
        "For any physical shipments, AI4Bazaar partners with reliable courier services to ensure timely delivery. Please ensure that the delivery address is correct at the time of purchase to avoid shipping delays or issues."
      ]
    },
    {
      title: "Delivery Delays",
      content: [
        "While we make every effort to deliver services and products on time, delays can occasionally occur due to unforeseen circumstances such as technical issues, third-party service delays, or global disruptions. In such cases, we will inform you promptly and provide an updated timeline.",
        "AI4Bazaar is not responsible for any delays caused by external factors like network outages or courier service delays for physical products. However, we remain committed to resolving any issues that affect the timely delivery of services or products."
      ]
    },
    {
      title: "Tracking and Updates",
      content: [
        "For digital services, you will receive regular updates via email or through the AI4Bazaar dashboard on the progress of your website development, e-commerce integration, or marketing campaigns.",
        "For any physical products or hardware that require shipping, you will receive a tracking number to monitor the shipment's progress. You can use the tracking number on the respective courier's website to check delivery status."
      ]
    },
    {
      title: "Refunds and Cancellations",
      content: [
        "If there are any issues with the payment or service delivery, please refer to our Refund and Cancellation Policy for detailed instructions on how to request a refund or cancel services. Refunds for services will be processed in accordance with the terms outlined in the refund policy."
      ]
    },
    {
      title: "Conclusion",
      content: [
        "AI4Bazaar is committed to providing a seamless billing and service delivery experience for all our clients. Whether you're adopting our digital solutions for your business or seeking e-commerce or digital marketing services, we aim to deliver high-quality services with transparency, security, and ease of use. If you have any questions about your billing or service delivery, please feel free to reach out to our support team at support@ai4bazaar.com."
      ]
    }
  ];

  return (
    <div className="legal-document">
      <motion.div 
        ref={containerRef}
        className="terms-container"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.header 
          className="legal-header"
          variants={fadeInVariants}
          whileHover={{ scale: 1.02 }}
        >
          <motion.h1 
            variants={itemVariants}
            whileHover={{ color: "#2c5282" }}
          >
            Shipping and Billing Process
          </motion.h1>
          <motion.p 
            className="effective-date"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </motion.header>

        <motion.div 
          className="legal-content" 
          variants={containerVariants}
        >
          {sections.map((section, index) => (
            <motion.article 
              key={index} 
              className="legal-section"
              variants={itemVariants}
              whileHover="hover"
              initial="hidden"
              animate={controls}
            >
              <motion.h2
                whileHover={{ x: 5, color: "#2b6cb0" }}
              >
                {section.title}
              </motion.h2>
              <motion.div 
                className="legal-text"
                variants={containerVariants}
              >
                {section.content.map((paragraph, pIndex) => (
                  <motion.p 
                    key={pIndex}
                    variants={itemVariants}
                    whileHover={{ x: 3 }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </motion.div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div 
          className="legal-footer"
          variants={fadeInVariants}
        >
          <motion.p whileHover={{ scale: 1.01 }}>
            If you have any questions about our shipping or billing processes, please contact us at support@ai4bazaar.com.
          </motion.p>
          <motion.p 
            className="copyright"
            whileHover={{ scale: 1.02 }}
          >
            © {new Date().getFullYear()} <b>VISYS CLOUD TECHNOLOGIES INDIA.</b> All rights reserved.
          </motion.p>
        </motion.div>
      </motion.div>
      <Footer />
    </div>
  );
};

export default ShipmentAndBilling;